import React, { useState, useCallback } from "react";
import {
  Box,
  TextField,
  Stack,
  Button,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Pagination,
  PaginationItem,
  InputLabel,
  CircularProgress,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import ReusableTable from "../../../components/ReusableTable/ReusableTable";
import { GET_ACTIVITY_LOG } from "../../../apis/settingsApis/Auditlogs-apis/auditLogsApis";
import { GET_STAFF_DETAILS } from "../../../apis/organization-apis/people-apis/peopleApis";
import { GET_SECURITY_ROLES_DETAILS } from "../../../apis/organization-apis/securityRoles/securityRolesApi";
import moment from "moment";
import { getDetails, getLogName, logNames } from "../../../data/data";
import DatePicker from "../../../components/DatePicker/DatePicker";
import { getStartOfDayUTC, getEndOfDayUTC } from "../../../utils/dateUtils";

const AuditLogs = () => {
  const [pageId, setPageId] = useState(1);
  const [limit, setLimit] = useState(10);

  // Separate filter input states
  const [filterInputs, setFilterInputs] = useState({
    activityName: "",
    dateRange: { startDate: "", endDate: "" },
  });

  // Query variables state
  const [queryVariables, setQueryVariables] = useState({
    pageId,
    limit,
  });

  const { data: roleData, loading: roleLoading } = useQuery(
    GET_SECURITY_ROLES_DETAILS
  );

  const { data: staffList, loading: employeeLoading } =
    useQuery(GET_STAFF_DETAILS);

  const { data, loading, error } = useQuery(GET_ACTIVITY_LOG, {
    variables: queryVariables,
    fetchPolicy: "network-only",
  });

  const handleFilter = useCallback(() => {
    const newQueryVariables = {
      pageId: 1,
      limit,
      activityName: filterInputs.activityName,
    };

    if (filterInputs.dateRange?.startDate || filterInputs.dateRange?.endDate) {
      newQueryVariables.dateRange = {
        startDate: filterInputs.dateRange?.startDate ? getStartOfDayUTC(filterInputs.dateRange.startDate) : null,
        endDate: filterInputs.dateRange?.endDate ? getEndOfDayUTC(filterInputs.dateRange.endDate) : null,
      };
    }

    setQueryVariables(newQueryVariables);
    setPageId(1);
  }, [filterInputs, limit]);

  const handlePageChange = (event, newPage) => {
    setPageId(newPage);
    setQueryVariables((prev) => ({
      ...prev,
      pageId: newPage,
    }));
  };

  const handleRowsPerPageChange = (event) => {
    const newLimit = event.target.value;
    setLimit(newLimit);
    setPageId(1);
    setQueryVariables((prev) => ({
      ...prev,
      limit: newLimit,
      pageId: 1,
    }));
  };

  const handleActivityNameChange = (event) => {
    const activityName = event.target.value;
    setFilterInputs((prev) => ({
      ...prev,
      activityName,
    }));
    setQueryVariables((prev) => ({
      ...prev,
      activityName,
      pageId: 1,
    }));
    setPageId(1);
  };

  const columns = [
    { label: "Performer", field: "employeeName" },
    { label: "Activity", field: "activityLogName" },
    { label: "Active", field: "isActive" },
    { label: "Created At", field: "createdAt" },
    { label: "Details", field: "activityLogActionDetails" },
  ];

  const tableData = data?.getActivityLog?.activityLogList || [];

  const filteredData = [...tableData].sort((a, b) => {
    const dateA = new Date(a.createdAt);
    const dateB = new Date(b.createdAt);
    return dateB - dateA; // Sort in descending order
  });

  const totalCounts = data?.getActivityLog?.activityLogCount || 0;

  const renderCell = (row, column) => {
    switch (column.field) {
      case "createdAt":
        return moment(row?.createdAt).format("lll");
      case "isActive":
        return row?.isActive ? "YES" : "No";
      case "activityLogName":
        return getLogName(row?.activityLogName);
      case "activityLogActionDetails":
        return getDetails(
          row?.activityLogActionDetails,
          row?.activityLogName,
          staffList,
          roleData
        );
      default:
        return row[column.field];
    }
  };

  return (
    <Box>
      {/* Filters Section */}
      <Stack direction="row" spacing={2} mb={3} justifyContent="flex-end">
        <FormControl>
          <InputLabel id="log-name-selector-label" shrink>
            Activity Name
          </InputLabel>
          <Select
            labelId="log-name-selector-label"
            value={filterInputs.activityName}
            onChange={handleActivityNameChange}
            label="Activity Name"
            size="small"
            displayEmpty
            sx={{ minWidth: 250 }}
          >
            <MenuItem value="">All</MenuItem>
            {logNames.map((logName) => (
              <MenuItem key={logName} value={logName}>
                {getLogName(logName)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <DatePicker
          label="Start Date"
          value={filterInputs.dateRange.startDate}
          onChange={(date) =>
            setFilterInputs((prev) => ({
              ...prev,
              dateRange: { ...prev.dateRange, startDate: date },
            }))
          }
          size="small"
        />
        <DatePicker
          label="End Date"
          value={filterInputs.dateRange.endDate}
          onChange={(date) =>
            setFilterInputs((prev) => ({
              ...prev,
              dateRange: { ...prev.dateRange, endDate: date },
            }))
          }
          size="small"
          disabled={!filterInputs.dateRange.startDate}
        />
        <Button
          variant="contained"
          onClick={handleFilter}
          disabled={loading || !filterInputs.dateRange.endDate}
        >
          Filter
        </Button>
      </Stack>

      {/* Table Section */}
      {loading || roleLoading || employeeLoading ? (
        <Box
          sx={{
            height: "475px",
            border: "1px solid #E0E0E0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <ReusableTable
          columns={columns}
          data={filteredData}
          renderCell={renderCell}
          rowsPerPageOptions={[10, 20, 40, 80]}
          height="475px"
          showPagination={false}
        />
      )}

      {/* Pagination Controls */}
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ padding: 2 }}
      >
        <FormControl variant="outlined" size="small">
          <Select value={limit} onChange={handleRowsPerPageChange}>
            {[10, 20, 40, 80, 100, 120].map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Pagination
          count={Math.ceil(totalCounts / limit)}
          page={pageId}
          onChange={handlePageChange}
          variant="outlined"
          shape="rounded"
          renderItem={(item) => (
            <PaginationItem
              {...item}
              sx={{
                background: item.selected ? "#031B89 !important" : "",
                color: item.selected ? "#fff !important" : "",
              }}
            />
          )}
        />
      </Stack>

      {error && (
        <Typography color="error">
          Error loading data: {error.message}
        </Typography>
      )}
    </Box>
  );
};

export default AuditLogs;
